import React, { useEffect, useReducer, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import {
  personnelShowPageInitialStore,
  personnelShowPageReducer,
} from 'reducers/personnelShowPageReducer';

import setupPolling from 'utilities/setupPolling';

import useForm from 'components/hooks/useForm';
import useModal from 'components/hooks/useModal';
import usePersonnelForm from 'components/hooks/usePersonnelForm';
import useProcessingAttachments from 'components/hooks/useProcessingAttachments';
import useProfilePhotoUploading from 'components/hooks/useProfilePhotoUploading';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';
import useTrackedPersonnel from 'components/hooks/useTrackedPersonnel';

import DestroyModal from 'components/application/DestroyModal';
import Portal from 'components/application/Portal';
import SuccessToast from 'components/application/SuccessToast';
import UndoableToast from 'components/application/UndoableToast';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { TrackedPersonnelContext } from 'components/contexts/TrackedPersonnelContext';
import { TrainingRegisterResourceManagementContext } from 'components/contexts/TrainingRegisterResourceManagementContext';
import ArchiveModal from 'components/personnel/ArchiveModal';
import PersonnelPanel from 'components/personnel/PersonnelPanel';
import PersonnelSidePanel from 'components/personnel/PersonnelSidePanel';
import ProfilePhotoModal from 'components/personnel/ProfilePhotoModal';
import TrackedPersonnelUsageBanner from 'components/personnel/TrackedPersonnelUsageBanner';
import UnarchiveBlockedModal from 'components/personnel/UnarchiveBlockedModal';
import BookingDestroyModal from 'components/training/BookingDestroyModal';
import BookingELearningModal from 'components/training/BookingELearningModal';
import BookingELearningNoCreditsModal from 'components/training/BookingELearningNoCreditsModal';
import BookingReminderModal from 'components/training/BookingReminderModal';
import BookingSidePanel from 'components/training/BookingSidePanel';
import CourseDestroyModal from 'components/training/CourseDestroyModal';
import SendInvitationModal from 'components/training/SendInvitationModal';
import TrainingDestroyModal from 'components/training/TrainingDestroyModal';
import TrainingRecords from 'components/training/TrainingRecords';
import TrainingRequirementsSidePanel from 'components/training/TrainingRequirementsSidePanel';
import TrainingSidePanel from 'components/training/TrainingSidePanel';

import { sendAnalytics } from 'components/helpers/analytics';
import {
  buildTeamSelectionsFromDomainTeams,
  haveProfilePhotoAttributesChanged,
  personnelParams,
  sortRoleIdsByPosition,
  sortRolesByPrimaryAndPosition,
} from 'components/helpers/personnel';
import {
  calculateTrainingExpiryDate,
  defaultTrainingStartDate,
  inferredTrainingExpiryDates,
} from 'components/helpers/resources/training';
import { personDisplayName } from 'components/helpers/users';

const defaultSubcontractor = { subcontractorName: '' };

const defaultTraining = {
  id: null,
  personnelId: '',
  courseId: '',
  course: null,
  startDate: null,
  expiryDate: null,
  notes: '',
  attachmentIds: [],
};

const defaultTrainingRequirement = {
  userCourseId: null,
  isCourseRequired: true,
  isRecordingTraining: false,
};

const defaultBooking = {
  date: '',
  courseId: '',
  notes: '',
  autoEnrolled: null,
};

const defaultBookingELearningModal = {
  isOpen: false,
  course: null,
  eLearningCourse: null,
};
const defaultBookingELearningNoCreditsModal = { isOpen: false };

export default function PersonnelShowPage(props) {
  const breadBoard = useBreadBoard();
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();

  const trainingRegisterContextValues = (({
    hasBookingEditableAccess,
    hasBookingGroupViewableAccess,
    hasBookingRemindableAccess,
    hasPersonnelEditableAccess,
    hasPersonnelProfileAccessEnabled,
    hasProfileAccessEditableAccess,
    hasRoleEditableAccess,
    hasTeamPersonnelEditableAccess,
    hasTeamViewableAccess,
    hasTrainingEditableAccess,
    isAutoEnrolmentIncludingMissingTraining,
    isViewingOwnProfile,
    personnelId,
    trainingReportPermittedOptions,
  }) => ({
    personnelId,
    isViewingOwnProfile,
    isAutoEnrolmentIncludingMissingTraining,
    trainingReportPermittedOptions,
    hasPersonnelProfileAccessEnabled,
    hasProfileAccessEditableAccess,
    hasPersonnelEditableAccess,
    hasRoleEditableAccess,
    hasTeamPersonnelEditableAccess,
    hasTeamViewableAccess,
    hasBookingEditableAccess,
    hasBookingRemindableAccess,
    hasTrainingEditableAccess,
    hasBookingGroupViewableAccess,
  }))(props);

  const [store, dispatch] = useReducer(
    personnelShowPageReducer,
    personnelShowPageInitialStore,
  );

  const [coursesMeta, setCoursesMeta] = useState({ loaded: false, info: {} });

  const [trainingEvidenceProcessed, setTrainingEvidenceProcessed] =
    useState(false);

  // application
  const [currentSubcontractor, setCurrentSubcontractor] =
    useForm(defaultSubcontractor);

  const [isSubcontractor, setIsSubcontractor] = useState(false);

  const [newCompanyRoles, setNewCompanyRoles] = useForm([]);

  const [
    currentTraining,
    setCurrentTraining,
    handleTrainingInputChange,
    ,
    ,
    handleTrainingDateChange,
  ] = useForm(defaultTraining);

  const [
    currentTrainingRequirement,
    setCurrentTrainingRequirement,
    handleTrainingRequirementInputChange,
  ] = useForm(defaultTrainingRequirement);

  const [
    currentBooking,
    setCurrentBooking,
    handleBookingInputChange,
    ,
    ,
    handleBookingDateChange,
  ] = useForm(defaultBooking);

  const [
    formPersonnel,
    setFormPersonnel,
    handlePersonnelInputChange,
    handlePersonnelOptionChange,
    handlePersonnelDateChange,
    handleFieldValueInputChange,
    handleDateFieldChange,
    handleFieldOptionChange,
    handleToggle,
    handleProfilePhotoRemoval,
    handleProfilePhotoMediaChange,
    handleNewProfilePhotoUploaded,
    handleProfilePhotoCropReady,
  ] = usePersonnelForm({});

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [eLearningAllowance, setELearningAllowance] = useState({
    loaded: false,
    currentCredits: null,
    hasError: false,
  });

  const {
    hasReachedTrackedPersonnelLimit,
    hasReachedTrackedPersonnelUsageThreshold,
    isTrackedPersonnelUsageLoaded,
    trackedPersonnelUsage,
  } = useTrackedPersonnel();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'pages.PersonnelShowPage',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { t: i18nTComponentsShared } = useTranslation('components', {
    keyPrefix: 'personnel.shared',
  });

  const handleTrainingCalendarClose = (calendarName) => {
    setCurrentTraining({
      ...currentTraining,
      ...inferredTrainingExpiryDates({
        domainCourse: currentTraining.course,
        expiryDates: {
          startDate: currentTraining.startDate,
          expiryDate: currentTraining.expiryDate,
        },
        dateToInferFrom: calendarName,
      }),
    });
  };

  // ui
  const [
    isTrainingSidePanelOpen,
    setIsTrainingSidePanelOpen,
    openTrainingSidePanel,
    closeTrainingSidePanel,
    _resetTrainingSidePanelContext,
    trainingSidePanelContext,
    setTrainingSidePanelContext,
  ] = useSidePanel(false, 'new');
  const [
    isPersonnelSidePanelOpen,
    _setIsPersonnelSidePanelOpen,
    openPersonnelSidePanel,
    closePersonnelSidePanel,
    _resetPersonnelSidePanelContext,
    _personnelSidePanelContext,
    setPersonnelSidePanelContext,
  ] = useSidePanel(false, 'new');
  const [
    isTrainingRequirementsSidePanelOpen,
    setIsTrainingRequirementsSidePanelOpen,
    openTrainingRequirementsSidePanel,
    closeTrainingRequirementsSidePanel,
    _resetTrainingRequirementsSidePanel,
    trainingRequirementsSidePanelContext,
  ] = useSidePanel(false, 'new');

  const [
    isBookingSidePanelOpen,
    _setIsBookingSidePanelOpen,
    openBookingSidePanel,
    closeBookingSidePanel,
    _resetBookingSidePanelContext,
    bookingSidePanelContext,
    setBookingSidePanelContext,
  ] = useSidePanel(false, 'new');

  const [isCourseDestroyModalOpen, setIsCourseDestroyModalOpen] =
    useState(false);

  const [isDestroyModalOpen, setIsDestroyModalOpen] = useState(false);

  const [isTrainingDestroyModalOpen, setIsTrainingDestroyModalOpen] =
    useState(false);

  const [
    isBookingDestroyModalOpen,
    ,
    openBookingDestroyModal,
    closeBookingDestroyModal,
  ] = useModal(false);

  const [bookingELearningModal, setBookingELearningModal] = useState(
    defaultBookingELearningModal,
  );
  const [bookingELearningNoCreditsModal, setBookingELearningNoCreditsModal] =
    useState(defaultBookingELearningNoCreditsModal);
  const [
    isBookingReminderModalOpen,
    ,
    openBookingReminderModal,
    closeBookingReminderModal,
  ] = useModal(false);
  const [isArchiveModalOpen, , openArchiveModal, closeArchiveModal] =
    useModal(false);
  const [
    isUnarchiveBlockedModalOpen,
    ,
    openUnarchiveBlockedModal,
    closeUnarchiveBlockedModal,
  ] = useModal(false);
  const [
    isSendInvitationModalOpen,
    ,
    openSendInivitationModal,
    closeSendInivitationModal,
  ] = useModal(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const {
    isProfilePhotoUploading,
    processProfilePhoto,
    resetProfilePhotoUploadState,
  } = useProfilePhotoUploading({
    getCurrentProfilePhoto: () => formPersonnel.profilePhoto,
  });

  // use effects

  useEffect(() => {
    if (currentActor.isAllowedFeature('e_learning')) {
      fetchELearningAllowance();
    }
  }, []);

  useEffect(() => {
    fetchAssignableCompanies();
    fetchAssignableCompanyRoles();
    fetchPersonnelTraining();
    fetchPersonnel();
    fetchPersonnelEvents();
    fetchCoursesMeta();
  }, []);

  useEffect(() => {
    setTrainingEvidenceProcessed(false);
  }, [JSON.stringify(store.domain.attachments)]);

  useProcessingAttachments({
    attachments: Object.values(store.domain.attachments),
    onProcessed: ({ attachment, coverImages }) =>
      dispatch({
        type: 'UPDATE_ATTACHMENT',
        payload: { attachment, coverImages },
      }),
  });

  // data management

  const formatPersonnelData = ({ data, fieldValues, profilePhoto, user }) => {
    if (!data) return;

    const { attributes } = data;
    const lineManagerData = data.relationships.lineManager.data;
    const companyRolesData = data.relationships.companyRoles.data;

    const formattedFieldValues = Object.values(fieldValues).reduce(
      (list, value) => {
        list[value.relationships.fieldAttribute.data.id] = {
          id: value.id,
          value:
            value.attributes.valueType === 'date' ?
              moment.parseZone(value.attributes.value).toDate()
            : value.attributes.value,
          valueType: value.attributes.valueType,
          fieldAttributeId: value.relationships.fieldAttribute.data.id,
          fieldOptionId:
            value.relationships.fieldOption.data &&
            value.relationships.fieldOption.data.id,
        };

        return list;
      },
      {},
    );

    return {
      id: data.id,
      firstName: attributes.firstName || '',
      lastName: attributes.lastName || '',
      externalId: attributes.externalId || '',
      email: attributes.email || '',
      lineManagerId: lineManagerData ? lineManagerData.id : '',
      fieldValues: formattedFieldValues,
      company: {
        companyId: data.relationships.company.data?.id || '',
        subcontractorId: data.relationships.subcontractor.data?.id || '',
      },
      primaryCompanyRoleId: '',
      companyRoleIds:
        companyRolesData ?
          companyRolesData.map((companyRole) => companyRole.id)
        : [],
      archivedAt: attributes.archivedAt,
      isParticipatingInOngoingProjects:
        data.meta?.isParticipatingInOngoingProjects,
      profileAccessEnabled: !!user?.attributes?.profileAccessEnabled,
      isFromAutoEnrollableDivision: data.meta?.isFromAutoEnrollableDivision,
      profilePhoto: { ...profilePhoto?.attributes, ...profilePhoto?.links },
    };
  };

  const handleNewCompanyRoleAdd = (newRole) => {
    setNewCompanyRoles(newCompanyRoles.concat([newRole]));
  };

  const assignResources = ({ data, included }) => {
    const fieldValues = included.reduce((object, resource) => {
      if (resource.type === 'fieldValue') {
        object[resource.id] = resource;
      }

      return object;
    }, {});

    const user = included.find(
      (resource) =>
        resource.type === 'user' &&
        resource.id === data.relationships.user.data.id,
    );
    const profilePhoto = included.find(
      (resource) =>
        resource.type === 'profilePhoto' &&
        resource.id === data.relationships.profilePhoto.data.id,
    );

    const orderedIncludedTeams = included
      .filter((resource) => resource.type === 'team')
      .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));

    const teamSelections = buildTeamSelectionsFromDomainTeams({
      domainTeams: orderedIncludedTeams,
      user,
    });

    const personnelData = formatPersonnelData({
      data,
      fieldValues,
      profilePhoto,
      user,
    });

    const primaryRole = included.find(
      ({ attributes, type }) =>
        type === 'userCompanyRole' && attributes.primary,
    );

    const formPersonnelParams = {
      ...personnelData,
      primaryCompanyRoleId:
        primaryRole ? primaryRole.relationships.companyRole.data.id : '',
      profilePhoto: {
        ...profilePhoto?.attributes,
        id: profilePhoto?.id,
      },
      teamSelections,
    };

    setFormPersonnel(formPersonnelParams);

    dispatch({
      type: 'LOAD_PERSONNEL',
      payload: {
        member: data,
        included: {
          companyRoles: included.filter(
            (resource) => resource.type === 'companyRole',
          ),
          courseCompanyRoles: included.filter(
            (resource) => resource.type === 'courseCompanyRole',
          ),
          userCompanyRoles: included.filter(
            (resource) => resource.type === 'userCompanyRole',
          ),
          userCourses: included.filter(
            (resource) => resource.type === 'userCourse',
          ),
          personnelCourseConnectionRequirementPriorities: included.filter(
            (resource) =>
              resource.type === 'personnelCourseConnectionRequirementPriority',
          ),
          autoEnrolmentConfigurations: included.filter(
            (resource) => resource.type === 'autoEnrolmentConfiguration',
          ),
          autoEnrolmentExclusions: included.filter(
            (resource) => resource.type === 'autoEnrolmentExclusion',
          ),
          lineManagers: included.filter(
            (resource) => resource.type === 'lineManager',
          ),
          companies: included.filter((resource) => resource.type === 'company'),
          subcontractors: included.filter(
            (resource) => resource.type === 'subcontractor',
          ),
          courses: included.filter((resource) => resource.type === 'course'),
          eLearningCourses: included.filter(
            (resource) => resource.type === 'eLearningCourse',
          ),
          bookings: included.filter((resource) => resource.type === 'booking'),
          teams: included.filter((resource) => resource.type === 'team'),
          registrations: included.filter(
            (resource) => resource.type === 'registration',
          ),
          fieldValues: included.filter(
            (resource) => resource.type === 'fieldValue',
          ),
          bookers: included.filter(
            (resource) => resource.type === 'simpleUser',
          ),
          users: included.filter((resource) => resource.type === 'user'),
          simpleUsers: included.filter(
            (resource) => resource.type === 'simpleUser',
          ),
          profilePhotos: included.filter(
            (resource) => resource.type === 'profilePhoto',
          ),
        },
      },
    });
  };

  const refreshResources = () => {
    fetchPersonnelTraining();
    fetchPersonnelEvents();
  };

  const handleAssignableRoleAdd = (role) => {
    dispatch({ type: 'UPDATE_ASSIGNABLE_ROLES', payload: { role } });
  };

  // computed
  const hasELearningBookings =
    currentActor.isAllowedFeature('e_learning') &&
    store.domain.personnel &&
    store.domain.personnel.meta.eLearningBookingsCount > 0;
  const currentBookings = Object.values(store.domain.bookings);
  const allTraining = store.domain.training.allDataIds.map(
    (trainingId) => store.domain.training.data[trainingId],
  );

  const displayPersonnel = formatPersonnelData({
    data: store.domain.personnel,
    fieldValues: store.domain.fieldValues,
    user: store.domain.users[
      store.domain.personnel?.relationships?.user?.data?.id
    ],
    profilePhoto:
      store.domain.profilePhotos[
        store.domain.personnel?.relationships?.profilePhoto?.data?.id
      ],
  });

  const selectedProfilePhoto =
    store.domain.profilePhotos[formPersonnel?.profilePhoto?.id];

  const isArchived = displayPersonnel && !!displayPersonnel.archivedAt;
  const isUnarchivingBlocked =
    isArchived && hasReachedTrackedPersonnelLimit && allTraining.length > 0;
  const isCurrentPersonnelTracked =
    !isArchived && (currentBookings.length > 0 || allTraining.length > 0);
  const selectedBooking = store.domain.bookings[selectedBookingId];
  const selectedBooker =
    selectedBooking && selectedBooking.relationships.booker.data ?
      store.domain.bookers[selectedBooking.relationships.booker.data.id]
    : null;
  const selectedBookingCourse =
    selectedBooking ?
      store.domain.courses[selectedBooking.relationships.course.data.id]
    : store.domain.courses[currentBooking.courseId];
  const selectedBookingGroupId =
    selectedBooking?.relationships?.bookingGroup?.data?.id;
  const selectedBookingAutoEnrolmentConfigurationId =
    selectedBookingCourse &&
    selectedBookingCourse.relationships?.autoEnrolmentConfiguration?.data?.id;
  const selectedBookingAutoEnrolmentExclusion =
    selectedBookingAutoEnrolmentConfigurationId &&
    Object.values(store.domain.autoEnrolmentExclusions).find(
      (exclusion) =>
        exclusion.relationships.autoEnrolmentConfiguration.data.id ===
        selectedBookingAutoEnrolmentConfigurationId,
    );
  const currentProfilePhoto = formPersonnel.profilePhoto;
  const orderedDomainTeams =
    store.domain.personnel?.relationships?.teams?.data
      ?.map((t) => store.domain.teams[t.id])
      ?.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name)) ||
    [];

  const fetchPersonnel = () => {
    axios
      .get(`/personnel/${props.personnelId}?with_course_connections=true`)
      .then((response) => assignResources(response.data))
      .catch(breadBoard.addInedibleToast);
  };

  const fetchAssignableCompanies = () => {
    axios
      .get('/assignable_companies')
      .then((response) =>
        dispatch({
          type: 'LOAD_ASSIGNABLE_COMPANIES',
          payload: { collection: response.data.data },
        }),
      )
      .catch(breadBoard.addInedibleToast);
  };

  const fetchAssignableCompanyRoles = () => {
    axios
      .get('/assignable_company_roles')
      .then((response) =>
        dispatch({
          type: 'LOAD_ASSIGNABLE_ROLES',
          payload: { collection: response.data.data },
        }),
      )
      .catch(breadBoard.addInedibleToast);
  };

  const { data: bookingGroupPersonnelMeta } = useQuery({
    queryKey: ['bookingGroup', selectedBookingGroupId, 'personnel'],
    queryFn: async () => {
      const response = await axios.get(
        `/booking_groups/${selectedBookingGroupId}/personnel?meta_only=true`,
        { params: { with_booking_write_eligibility: true } },
      );
      return response.data.meta;
    },
    enabled:
      currentActor.user.attributes.accessType !== 'personnel' &&
      !!selectedBookingGroupId,
  });

  const fetchPersonnelTraining = () => {
    axios
      .get(`/personnel/${props.personnelId}/trainings`)
      .then((response) => {
        dispatch({
          type: 'LOAD_TRAINING',
          payload: {
            collection: response.data.data,
            included: {
              courses: response.data.included.filter(
                (obj) => obj.type === 'course',
              ),
              attachments: response.data.included.filter(
                (obj) => obj.type === 'attachment',
              ),
              coverImages: response.data.included.filter(
                (obj) => obj.type === 'coverImage',
              ),
              eLearningCourses: response.data.included.filter(
                (obj) => obj.type === 'eLearningCourse',
              ),
              autoEnrolmentConfigurations: response.data.included.filter(
                (resource) => resource.type === 'autoEnrolmentConfiguration',
              ),
            },
          },
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  const fetchCoursesMeta = () => {
    axios
      .get('/courses/?meta_only=true')
      .then((response) => {
        setCoursesMeta({
          loaded: true,
          info: response.data.meta,
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  const fetchPersonnelEvents = (page = 1) => {
    if (props.hasPersonnelEventViewableAccess) {
      axios
        .get(`/personnel/${props.personnelId}/events`, {
          params: { page },
        })
        .then((response) => {
          dispatch({
            type: 'LOAD_PERSONNEL_EVENTS',
            payload: {
              collection: response.data.data,
              meta: response.data.meta,
              included: {
                personnelEventUsers: response.data.included.filter(
                  (obj) => obj.type === 'user',
                ),
                personnelEventProfilePhotos: response.data.included.filter(
                  (obj) => obj.type === 'profilePhoto',
                ),
              },
            },
          });
        });
    } else {
      dispatch({
        type: 'LOAD_PERSONNEL_EVENTS',
        payload: { collection: [], meta: {}, included: {} },
      });
    }
  };

  const fetchELearningAllowance = () => {
    axios
      .get('/e_learning/allowance')
      .then((response) =>
        setELearningAllowance({
          ...eLearningAllowance,
          loaded: true,
          currentCredits: response.data.data.attributes.currentCredits,
        }),
      )
      .catch((_error) =>
        setELearningAllowance({ ...eLearningAllowance, hasError: true }),
      );
  };

  const createTraining = () => {
    axios
      .post('/trainings', {
        training: {
          personnel_id: currentTraining.personnelId,
          course_id: currentTraining.courseId,
          start_date: moment
            .parseZone(currentTraining.startDate)
            .format('DD/MM/YYYY'),
          expiry_date:
            currentTraining.expiryDate &&
            moment.parseZone(currentTraining.expiryDate).format('DD/MM/YYYY'),
          notes: currentTraining.notes,
          attachment_ids: currentTraining.attachmentIds,
        },
      })
      .then(() => {
        const trainingBooking = currentBookings.find(
          (booking) =>
            booking.relationships.course.data.id === currentTraining.courseId,
        );

        if (trainingBooking) {
          const course = store.domain.courses[currentTraining.courseId];
          const isELearningCourse = !!course.relationships.eLearningCourse.data;
          dispatch({
            type:
              isELearningCourse ?
                'DELETE_E_LEARNING_BOOKING'
              : 'DELETE_BOOKING',
            payload: { member: trainingBooking },
          });
          if (isELearningCourse) fetchELearningAllowance();
        }

        refreshResources();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        setCurrentBooking(defaultBooking);
        closeTrainingSidePanel();
      })
      .catch(handleRequestError);
  };

  const createTrainingRequirement = () => {
    const trainingRequirementParams = {
      personnel_id: currentTraining.personnelId,
      course_id: currentTraining.courseId,
      user_course_attributes: {
        required: currentTrainingRequirement.isCourseRequired,
      },
    };
    if (currentTrainingRequirement.isRecordingTraining === true) {
      trainingRequirementParams.training_attributes = {
        start_date: moment
          .parseZone(currentTraining.startDate)
          .format('DD/MM/YYYY'),
        expiry_date:
          currentTraining.expiryDate &&
          moment.parseZone(currentTraining.expiryDate).format('DD/MM/YYYY'),
        notes: currentTraining.notes,
        attachment_ids: currentTraining.attachmentIds,
      };
    }

    axios
      .post('/training_requirements', {
        training_requirement: trainingRequirementParams,
      })
      .then((response) => {
        dispatch({
          type: 'CREATE_TRAINING_REQUIREMENT',
          payload: {
            userCourse: response.data.included.find(
              (resource) => resource.type === 'userCourse',
            ),
            training: response.data.included.find(
              (resource) => resource.type === 'training',
            ),
            attachments: response.data.included.filter(
              (obj) => obj.type === 'attachment',
            ),
            coverImages: response.data.included.filter(
              (obj) => obj.type === 'coverImage',
            ),
            eLearningCourses: response.data.included.filter(
              (resource) => resource.type === 'eLearningCourse',
            ),
            course: response.data.included.find(
              (resource) => resource.type === 'course',
            ),
            autoEnrolmentConfiguration: response.data.included.find(
              (resource) => resource.type === 'autoEnrolmentConfiguration',
            ),
          },
        });
        fetchPersonnelEvents();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        closeTrainingRequirementsSidePanel();
      })
      .catch(handleRequestError);
  };

  const createAutoEnrolmentExclusion = ({
    autoEnrolmentConfigurationId,
    personnelId,
  }) => {
    axios
      .post('/auto_enrolment_exclusions', {
        auto_enrolment_exclusion: {
          personnel_id: personnelId,
          auto_enrolment_configuration_id: autoEnrolmentConfigurationId,
        },
      })
      .then((response) => {
        dispatch({
          type: 'CREATE_AUTO_ENROLMENT_EXCLUSION',
          payload: { member: response.data.data },
        });
      })
      .catch(handleRequestError);
  };

  const deleteAutoEnrolmentExclusion = ({ autoEnrolmentExclusion }) => {
    axios
      .delete(`/auto_enrolment_exclusions/${autoEnrolmentExclusion.id}`)
      .then((_response) => {
        dispatch({
          type: 'DELETE_AUTO_ENROLMENT_EXCLUSION',
          payload: { member: autoEnrolmentExclusion },
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  const updateTraining = () => {
    axios
      .patch(`/trainings/${currentTraining.id}`, {
        training: {
          start_date: moment
            .parseZone(currentTraining.startDate)
            .format('DD/MM/YYYY'),
          expiry_date: moment
            .parseZone(currentTraining.expiryDate)
            .format('DD/MM/YYYY'),
          notes: currentTraining.notes,
          attachment_ids: currentTraining.attachmentIds,
        },
      })
      .then((_response) => {
        refreshResources();
        closeTrainingSidePanel();
      })
      .catch(handleRequestError);
  };

  const deleteTraining = () => {
    axios
      .delete(`/trainings/${currentTraining.id}`)
      .then((_response) => {
        refreshResources();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
      })
      .catch(breadBoard.addInedibleToast);
  };

  const createUserCourse = ({ course, params }) => {
    const joinParams = {
      ...params,
      personnel_id: displayPersonnel.id,
      course_id: course.id,
    };

    axios
      .post('/user_courses', { user_course: joinParams })
      .then((response) => {
        const personnelCourseConnectionRequirementPriority =
          response.data.included.find(
            (resource) =>
              resource.type === 'personnelCourseConnectionRequirementPriority',
          );

        dispatch({
          type: 'CREATE_USER_COURSE',
          payload: {
            member: response.data.data,
            course: response.data.included.find(
              (resource) => resource.type === 'course',
            ),
            autoEnrolmentConfiguration: response.data.included.find(
              (resource) => resource.type === 'autoEnrolmentConfiguration',
            ),
            personnelCourseConnectionRequirementPriority,
            simpleUser: response.data.included.find(
              (resource) => resource.type === 'simpleUser',
            ),
          },
        });

        if (personnelCourseConnectionRequirementPriority) {
          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ bold: <span className='tw-font-medium' /> }}
                  i18nKey='personnel.requirementPriorityAdded'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    courseName: course.attributes.name,
                    personnelName: displayPersonnel.firstName,
                  }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        }

        fetchPersonnelEvents();
      })
      .catch(breadBoard.addInedibleToast);
  };

  const handleUserCourseUpdate = ({ params, userCourse }) => {
    axios
      .patch(`/user_courses/${userCourse.id}`, { user_course: params })
      .then((response) => {
        dispatch({
          type: 'UPDATE_USER_COURSE',
          payload: {
            member: response.data.data,
            personnelCourseConnectionRequirementPriority:
              response.data.included.find(
                (resource) =>
                  resource.type ===
                  'personnelCourseConnectionRequirementPriority',
              ),
            simpleUser: response.data.included.find(
              (resource) => resource.type === 'simpleUser',
            ),
          },
        });
        if (
          params.personnel_course_connection_requirement_priority_attributes
        ) {
          const requirementPriorityTranslationKey =
            (
              params.personnel_course_connection_requirement_priority_attributes
                ._destroy
            ) ?
              'personnel.requirementPriorityRemoved'
            : 'personnel.requirementPriorityAdded';

          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ bold: <span className='tw-font-medium' /> }}
                  i18nKey={requirementPriorityTranslationKey}
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    courseName:
                      store.domain.courses[
                        userCourse.relationships.course.data.id
                      ].attributes.name,
                    personnelName: displayPersonnel.firstName,
                  }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        }
        fetchPersonnelEvents();
      })
      .catch(breadBoard.addInedibleToast);
  };

  const deleteUserCourse = () => {
    const userCourse =
      store.domain.userCourses[currentTrainingRequirement.userCourseId];

    axios
      .delete(`/user_courses/${userCourse.id}`)
      .then((_response) => {
        dispatch({
          type: 'DELETE_USER_COURSE',
          payload: { member: userCourse },
        });
        fetchPersonnelEvents();
      })
      .catch(breadBoard.addInedibleToast);
  };

  const createBooking = (courseName) => {
    axios
      .post(`/personnel/${props.personnelId}/bookings`, {
        booking: {
          course_id: currentBooking.courseId,
          date: moment.parseZone(currentBooking.date).format('DD/MM/YYYY'),
          notes: currentBooking.notes,
        },
      })
      .then((response) => {
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='personnel.bookedOnCourse'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{ courseName, firstName: formPersonnel.firstName }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
        fetchPersonnelEvents();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        handleCloseBookingSidePanel();
        const payload = {
          member: response.data.data,
          booker: response.data.included.find((i) => i.type === 'simpleUser'),
        };
        dispatch({ type: 'CREATE_BOOKING', payload });
        setCurrentBooking(defaultBooking);
      })
      .catch(handleRequestError);
  };

  const createELearningBooking = ({ course }) => {
    axios
      .post(`/personnel/${props.personnelId}/bookings`, {
        booking: { course_id: course.id },
      })
      .then((response) => {
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='personnel.enrolledOnCourse'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  courseName: course.attributes.name,
                  firstName: formPersonnel.firstName,
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
        fetchPersonnelEvents();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        fetchELearningAllowance();
        dispatch({
          type: 'CREATE_E_LEARNING_BOOKING',
          payload: { member: response.data.data },
        });
      })
      .catch(breadBoard.addInedibleToast);
  };

  const deleteBooking = ({
    bookingId,
    isNotified = false,
    sendEmails = true,
  }) => {
    const booking = store.domain.bookings[bookingId];
    const course = store.domain.courses[booking.relationships.course.data.id];
    axios
      .delete(`/personnel/${props.personnelId}/bookings/${booking.id}`, {
        params: { send_emails: sendEmails },
      })
      .then((response) => {
        if (isNotified) {
          breadBoard.addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='bookings.removedForCourse'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    courseName: course.attributes.name,
                    firstName: formPersonnel.firstName,
                  }}
                />
              }
              onBurnToast={breadBoard.handleBurnToast}
            />,
          );
        }

        dispatch({
          type:
            course.relationships.eLearningCourse.data ?
              'DELETE_E_LEARNING_BOOKING'
            : 'DELETE_BOOKING',
          payload: { member: booking },
        });

        const includedAutoEnrolmentExclusion =
          response.data.included &&
          response.data.included.find(
            (resource) => resource.type === 'autoEnrolmentExclusion',
          );

        if (includedAutoEnrolmentExclusion) {
          dispatch({
            type: 'CREATE_AUTO_ENROLMENT_EXCLUSION',
            payload: { member: includedAutoEnrolmentExclusion },
          });
        }

        fetchPersonnelEvents();
        setCurrentBooking(defaultBooking);
        setSelectedBookingId(null);
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        handleCloseBookingSidePanel();

        if (course.relationships.eLearningCourse.data) {
          fetchELearningAllowance();
        }
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  const handlePersonnelUpdateSubmit = () => {
    const originalUserCompanyRoles =
      store.domain.personnel.relationships.userCompanyRoles.data.map(
        (ucr) => store.domain.userCompanyRoles[ucr.id],
      );
    const originalCompanyRoleIds =
      store.domain.personnel.relationships.companyRoles.data.map(
        (cr) => store.domain.companyRoles[cr.id].id,
      );

    const domainProfilePhotoFromRelationship =
      store.domain.profilePhotos[
        store.domain.personnel.relationships.profilePhoto?.data?.id
      ];

    const teamIds = formPersonnel.teamSelections.map((team) => team.id);
    const managedTeamIds = formPersonnel.teamSelections
      .filter((team) => team.isManager === true)
      .map((team) => team.id);

    const params = personnelParams({
      currentPersonnel: formPersonnel,
      fieldValues: formPersonnel.fieldValues,
      isSubcontractor,
      currentSubcontractor,
      newCompanyRoles,
      originalUserCompanyRoles,
      originalCompanyRoleIds,
      profilePhotos: {
        domain: selectedProfilePhoto,
        existingFromRelationship: domainProfilePhotoFromRelationship,
      },
      teamIds,
      managedTeamIds,
    });

    axios
      .patch(`/personnel/${formPersonnel.id}?with_course_connections=true`, {
        personnel: params,
      })
      .then((response) => {
        closePersonnelSidePanel();
        setCurrentSubcontractor(defaultSubcontractor);
        setNewCompanyRoles([]);
        fetchAssignableCompanies();
        fetchAssignableCompanyRoles();
        assignResources(response.data);
        resetRequestError();

        dispatch({ type: 'PROFILE_PHOTO_SAVED' });

        handleDomainProfilePhotoChange(formPersonnel, displayPersonnel);

        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey={`shared.success.${response.data.meta.userInvited ? 'updatedAndSentInvitation' : 'updated'}`}
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  name: personDisplayName({
                    attributes: response.data.data.attributes,
                  }),
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
      })
      .catch(handleRequestError);
  };

  const handleDomainProfilePhotoChange = (formPersonnel, displayPersonnel) => {
    if (
      !!formPersonnel.profilePhoto &&
      haveProfilePhotoAttributesChanged(
        displayPersonnel.profilePhoto,
        formPersonnel.profilePhoto,
      )
    ) {
      // poll for processed photo
      setupPolling({
        requestFn: () =>
          axios.get(`/profile_photos/${formPersonnel.profilePhoto.hashid}`),
        testFn: (response) => response.data.data.attributes.processed,
        intervalTime: 1000,
      })
        .startPolling()
        .then((response) => {
          handleNewProfilePhotoUploaded(response.data.data);
          dispatch({
            type: 'PROFILE_PHOTO_PROCESSING_COMPLETED',
            payload: { profilePhoto: response.data.data },
          });
        })
        .catch(() => {
          dispatch({ type: 'PROFILE_PHOTO_CHANGE_COMPLETED' });
        });
    } else {
      dispatch({ type: 'PROFILE_PHOTO_CHANGE_COMPLETED' });
    }
  };

  const archivePersonnel = ({ isToastMessageDisplayed = true }) => {
    axios
      .patch(`/personnel/${formPersonnel.id}?with_course_connections=true`, {
        personnel: { archived_at: moment().format('DD/MM/YYYY') },
      })
      .then((response) => {
        closeArchiveModal();
        assignResources(response.data);
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        fetchPersonnelEvents();
        sendAnalytics('Personnel archived', {
          currentUser: currentActor.user,
          personnelId: props.personnelId,
        });
        if (isToastMessageDisplayed) {
          breadBoard.addToast(
            <UndoableToast
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() =>
                unarchivePersonnel({ isToastMessageDisplayed: false })
              }
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.archived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    name: personDisplayName({
                      attributes: response.data.data.attributes,
                    }),
                  }}
                />
              }
            />,
          );
        }
      })
      .catch((_error) => {
        closeArchiveModal();
        breadBoard.addInedibleToast({
          fullMessage: i18nTCommon('personnel.errors.notArchived', {
            interpolation: { escapeValue: false },
            name: personDisplayName({ attributes: displayPersonnel }),
          }),
        });
      });
  };

  const unarchivePersonnel = ({ isToastMessageDisplayed = true }) => {
    axios
      .patch(`/personnel/${formPersonnel.id}?with_course_connections=true`, {
        personnel: { archived_at: null },
      })
      .then((response) => {
        closeArchiveModal();
        assignResources(response.data);
        fetchPersonnelEvents();
        queryClient.invalidateQueries(['personnel', 'trackedUsageReport']);
        sendAnalytics('Personnel unarchived', {
          currentUser: currentActor.user,
          personnelId: props.personnelId,
        });
        if (isToastMessageDisplayed) {
          breadBoard.addToast(
            <UndoableToast
              onBurnToast={breadBoard.handleBurnToast}
              onUndoAction={() =>
                archivePersonnel({ isToastMessageDisplayed: false })
              }
              toastText={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='shared.success.notArchived'
                  ns='common'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    name: personDisplayName({
                      attributes: response.data.data.attributes,
                    }),
                  }}
                />
              }
            />,
          );
        }
      })
      .catch((_error) => {
        closeArchiveModal();
        breadBoard.addInedibleToast({
          fullMessage: i18nTCommon('personnel.errors.notUnarchived', {
            interpolation: { escapeValue: false },
            name: personDisplayName({ attributes: displayPersonnel }),
          }),
        });
      });
  };

  const destroyPersonnel = () => {
    axios
      .delete(`/personnel/${formPersonnel.id}`)
      .then((_response) => window.location.replace('/personnel'))
      .catch(breadBoard.addInedibleToast);
  };

  function handleSendConfirmationClick() {
    closeSendInivitationModal();

    axios
      .post(`/personnel/${props.personnelId}/invites`)
      .then(() => {
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='shared.success.sentInvitation'
                ns='common'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  name: personDisplayName({ attributes: displayPersonnel }),
                }}
              />
            }
            onBurnToast={breadBoard.onBurnToast}
          />,
        );
      })
      .catch(breadBoard.addInedibleToast);
  }

  // handlers

  const handleCancel = () => {
    resetProfilePhotoUploadState();
    dispatch({ type: 'PROFILE_PHOTO_CANCELLED' });
    const profilePhoto =
      store.domain.profilePhotos[
        store.domain.personnel.relationships.profilePhoto?.data?.id
      ];

    const teamSelections = buildTeamSelectionsFromDomainTeams({
      domainTeams: orderedDomainTeams,
      user: store.domain.users[
        store.domain.personnel?.relationships?.user?.data?.id
      ],
    });

    setFormPersonnel({
      ...displayPersonnel,
      profilePhoto: {
        ...profilePhoto?.attributes,
        id: store.domain.personnel.relationships.profilePhoto?.data?.id,
      },
      teamSelections,
    });
    resetRequestError();
    closePersonnelSidePanel();
  };

  const handleCancelTraining = () => {
    closeTrainingSidePanel();
    resetRequestError();
  };

  const handleCloseBookingSidePanel = () => {
    resetRequestError();
    closeBookingSidePanel();
  };

  const handleTrainingEvidenceProcessed = () => {
    setTrainingEvidenceProcessed(true);
  };

  const handleNewTrainingClick = ({ booking, course }) => {
    closeBookingSidePanel();
    setTrainingSidePanelContext('new');
    resetRequestError();

    const startDate =
      booking?.attributes.date ? defaultTrainingStartDate({ booking }) : null;

    const expiryDate =
      startDate ? calculateTrainingExpiryDate({ course, startDate }) : null;

    setCurrentTraining({
      ...defaultTraining,
      personnelId: displayPersonnel.id,
      course,
      courseId: course.id,
      expiryDate,
      startDate,
    });

    openTrainingSidePanel();
  };

  const handleEditTrainingClick = ({ course, training }) => {
    setTrainingSidePanelContext('edit');
    resetRequestError();
    const trainingAttributes = training.attributes;
    setCurrentTraining({
      id: training.id,
      personnelId: training.relationships.personnel.data.id,
      courseId: training.relationships.course.data.id,
      course,
      startDate: moment.parseZone(trainingAttributes.startDate).toDate(),
      expiryDate:
        trainingAttributes.expiryDate &&
        moment.parseZone(trainingAttributes.expiryDate).toDate(),
      notes: trainingAttributes.notes,
      attachmentIds: training.relationships.attachments.data.map(({ id }) =>
        Number(id),
      ),
    });
    openTrainingSidePanel();
  };

  const handleAddBooking = ({ course }) => {
    setBookingSidePanelContext('new');
    resetRequestError();
    setCurrentBooking({
      ...defaultBooking,
      courseId: course.id,
      courseName: course.attributes.name,
      notes:
        course.attributes.defaultBookingNote ?
          course.attributes.defaultBookingNote
        : undefined,
    });
    setSelectedBookingId(null);
    openBookingSidePanel();
  };

  const handleViewBookingClick = (bookingId) => {
    setBookingSidePanelContext('show');
    resetRequestError();
    const editedBooking = store.domain.bookings[bookingId];
    setCurrentBooking({
      ...defaultBooking,
      id: editedBooking.id,
      date: moment.parseZone(editedBooking.attributes.date).toDate(),
      notes: editedBooking.attributes.notes,
    });
    setSelectedBookingId(bookingId);
    openBookingSidePanel();
  };

  function handleSendInvitationClick() {
    openSendInivitationModal();
  }

  const handleEditBookingClick = () => {
    setBookingSidePanelContext('edit');
  };

  const handleBookingUpdateSubmit = () => {
    axios
      .patch(`/personnel/${props.personnelId}/bookings/${currentBooking.id}`, {
        booking: {
          date: moment.parseZone(currentBooking.date).format('DD/MM/YYYY'),
          notes: currentBooking.notes,
        },
      })
      .then((response) => {
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey='pages.PersonnelShowPage.handleBookingUpdateSubmit.success'
                ns='components'
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  name: personDisplayName({ attributes: formPersonnel }),
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
        refreshResources();
        dispatch({
          type: 'UPDATE_BOOKING',
          payload: { member: response.data.data },
        });
        handleCloseBookingSidePanel();
      })
      .catch(handleRequestError);
  };

  const handleAddELearningBooking = ({ course, eLearningCourse }) => {
    if (
      !eLearningCourse.attributes.custom &&
      eLearningAllowance.loaded &&
      eLearningAllowance.currentCredits <= 0
    ) {
      setBookingELearningNoCreditsModal({ isOpen: true });
    } else {
      setBookingELearningModal({ isOpen: true, course, eLearningCourse });
    }
  };

  const handleRemoveBooking = (bookingId) => {
    if (bookingId && !selectedBookingId) {
      setSelectedBookingId(bookingId);
    }
    openBookingDestroyModal();
  };

  const handleBookingReminder = (bookingId) => {
    if (bookingId && !selectedBookingId) {
      setSelectedBookingId(bookingId);
    }
    openBookingReminderModal();
  };

  const handleDeleteTrainingClick = ({ course, training }) => {
    setCurrentTraining({
      ...defaultTraining,
      id: training.id,
      personnelId: displayPersonnel.id,
      courseId: course.id,
      course,
    });
    setIsTrainingDestroyModalOpen(true);
  };

  const handleDeleteCourseClick = ({ course, userCourse }) => {
    setCurrentTrainingRequirement({
      ...currentTrainingRequirement,
      userCourseId: userCourse.id,
    });
    setCurrentTraining({
      ...currentTraining,
      course,
    });
    setIsCourseDestroyModalOpen(true);
  };

  const sendBookingReminder = ({ booking, course }) => {
    const isCourseELearning = !!course.relationships.eLearningCourse.data;

    axios
      .post(`/bookings/${booking.id}/reminders`)
      .then(() => {
        breadBoard.addToast(
          <SuccessToast
            message={
              <Trans
                components={{ span: <span className='tw-font-medium' /> }}
                i18nKey={
                  isCourseELearning ?
                    'pages.PersonnelShowPage.sendBookingReminder.success.eLearning'
                  : 'personnel.emailNotifications.personnelCourseReminder'
                }
                ns={isCourseELearning ? 'components' : 'common'}
                tOptions={{ interpolation: { escapeValue: false } }}
                values={{
                  courseName: course.attributes.name,
                  firstName: formPersonnel.firstName,
                }}
              />
            }
            onBurnToast={breadBoard.handleBurnToast}
          />,
        );
        fetchPersonnelEvents();
      })
      .catch(() =>
        breadBoard.addInedibleToast({
          fullMessage: i18nT('sendBookingReminder.error'),
        }),
      );
  };

  const handleCourseSelectChange = ({ selected }) => {
    setCurrentTraining({
      ...defaultTraining,
      personnelId: displayPersonnel.id,
      courseId: selected.value.resource.id,
      course: selected.value.resource,
    });
    setCurrentTrainingRequirement(defaultTrainingRequirement);
    resetRequestError();
    openTrainingRequirementsSidePanel();
  };

  let personnelCompanyRoles = [];
  let courseCompanyRoles = [];
  if (!store.application.pending.personnelFetch) {
    const userCompanyRoles =
      store.domain.personnel.relationships.userCompanyRoles.data.map(
        (ucr) => store.domain.userCompanyRoles[ucr.id],
      );
    const companyRoles =
      store.domain.personnel.relationships.companyRoles.data.map(
        (cr) => store.domain.companyRoles[cr.id],
      );
    personnelCompanyRoles = sortRolesByPrimaryAndPosition(
      userCompanyRoles,
      companyRoles,
    );
    courseCompanyRoles =
      store.domain.personnel.relationships.courseCompanyRoles.data.map(
        (ccr) => store.domain.courseCompanyRoles[ccr.id],
      );
  }

  const handleArchiveButtonClick = () => {
    if (isUnarchivingBlocked) {
      openUnarchiveBlockedModal();
    } else {
      openArchiveModal();
    }
  };

  const handleCompanyRolesOptionChange = (values) => {
    if (
      formPersonnel.primaryCompanyRoleId === '' &&
      formPersonnel.companyRoleIds.length === 0
    ) {
      setFormPersonnel({
        ...formPersonnel,
        primaryCompanyRoleId: values[0].value,
        companyRoleIds: values.map((v) => v.value),
      });
    } else {
      setFormPersonnel({
        ...formPersonnel,
        companyRoleIds: values.map((v) => v.value),
      });
    }
  };

  function handleSelectPrimary(id) {
    setFormPersonnel({ ...formPersonnel, primaryCompanyRoleId: id });
  }

  function handleRemoveRole(id) {
    const filteredCompanyRoleIds = formPersonnel.companyRoleIds.filter(
      (roleId) => roleId !== id,
    );
    const newPrimaryCompanyRoleId =
      filteredCompanyRoleIds.length > 0 ?
        sortRoleIdsByPosition(
          store.domain.assignableRoles,
          filteredCompanyRoleIds,
        )[0]
      : '';
    setFormPersonnel({
      ...formPersonnel,
      companyRoleIds: filteredCompanyRoleIds,
      primaryCompanyRoleId:
        formPersonnel.primaryCompanyRoleId === id ?
          newPrimaryCompanyRoleId
        : formPersonnel.primaryCompanyRoleId,
    });
  }

  function handleDestroyModalClose() {
    if (selectedBookingCourse?.relationships?.eLearningCourse?.data?.id) {
      setCurrentBooking(defaultBooking);
      setSelectedBookingId(null);
    }
    closeBookingDestroyModal();
  }

  function handleReminderModalClose() {
    if (selectedBookingCourse?.relationships?.eLearningCourse?.data?.id) {
      setCurrentBooking(defaultBooking);
      setSelectedBookingId(null);
    }
    closeBookingReminderModal();
  }

  function handleSendInvitationModalClose() {
    closeSendInivitationModal();
  }

  function handleDeletePersonnelClick() {
    setIsDestroyModalOpen(true);
  }

  function handleSubcontractorSelect(selection) {
    setCurrentSubcontractor(selection);
  }

  function handleTeamSelect(team) {
    const teamSelection = {
      id: team.id,
      isManager: false,
      persisted: false,
    };

    setFormPersonnel({
      ...formPersonnel,
      teamSelections: [teamSelection, ...formPersonnel.teamSelections],
    });
  }

  function handleTeamSelectionRoleChange({ isManager, teamSelection }) {
    setFormPersonnel({
      ...formPersonnel,
      teamSelections: formPersonnel.teamSelections.map((t) =>
        t.id === teamSelection.id ? { ...t, isManager } : t,
      ),
    });
  }

  function handleTeamSelectionDelete(teamSelectionId) {
    setFormPersonnel({
      ...formPersonnel,
      teamSelections: formPersonnel.teamSelections.filter(
        (t) => t.id !== teamSelectionId,
      ),
    });
  }

  function handleDivisionOptionChange(selected) {
    const isCompanyIdPersisted =
      String(selected.value.companyId) ===
      String(store.domain.personnel.relationships.company.data.id);

    let teamSelections = [];

    if (isCompanyIdPersisted) {
      teamSelections = buildTeamSelectionsFromDomainTeams({
        domainTeams: orderedDomainTeams,
        user: store.domain.users[
          store.domain.personnel?.relationships?.user?.data?.id
        ],
      });
    }

    setFormPersonnel({
      ...formPersonnel,
      company: {
        companyId: selected.value.companyId,
        subcontractorId: '',
      },
      teamSelections,
    });
  }

  const isProfilePhotoChanging = store.application.pending.profilePhotoChange;
  const isProfilePhotoModalOpen =
    store.application.pending.profilePhotoModalOpen;

  const handleProcessProfilePhoto = (tempPhotoFile) => {
    processProfilePhoto(tempPhotoFile, {
      onSuccess: (profilePhotoData) => {
        handleNewProfilePhotoUploaded(profilePhotoData);
        dispatch({
          type: 'PROFILE_PHOTO_UPLOAD_COMPLETED',
          payload: { profilePhoto: profilePhotoData },
        });
      },
      onError: (error) => {
        const msg = error?.message || 'Profile photo upload failed';
        breadBoard.addInedibleToast({ fullMessage: msg });
        dispatch({ type: 'PROFILE_PHOTO_UPLOAD_CANCELLED' });
      },
    });
  };

  const handlePhotoModalClose = () => {
    dispatch({ type: 'PROFILE_PHOTO_MODAL_CLOSE' });
    resetProfilePhotoUploadState();

    const profilePhoto =
      store.domain.profilePhotos[
        store.domain.personnel.relationships.profilePhoto?.data?.id
      ];
    setFormPersonnel({
      ...formPersonnel,
      profilePhoto: {
        ...profilePhoto?.attributes,
        id: store.domain.personnel.relationships.profilePhoto?.data?.id,
      },
    });
  };

  return (
    !store.application.pending.assignableCompaniesFetch &&
    !store.application.pending.personnelFetch && (
      <TrackedPersonnelContext.Provider
        value={{
          hasReachedLimit: hasReachedTrackedPersonnelLimit,
          isCurrentPersonnelTracked,
          isTrackedPersonnelUsageLoaded,
          usage: trackedPersonnelUsage,
        }}
      >
        <div className='col-sm-3 tw-pl-0'>
          <TrainingRegisterResourceManagementContext.Provider
            value={trainingRegisterContextValues}
          >
            <PersonnelPanel
              currentPersonnel={displayPersonnel}
              division={
                store.domain.companies[
                  store.domain.personnel?.relationships?.company?.data?.id
                ]
              }
              isProfilePhotoLoading={isProfilePhotoChanging}
              lineManager={
                store.domain.lineManagers[displayPersonnel.lineManagerId]
              }
              onArchiveButtonClick={handleArchiveButtonClick}
              onDeleteClick={handleDeletePersonnelClick}
              onSendInvitationClick={handleSendInvitationClick}
              openSidePanel={openPersonnelSidePanel}
              personnel={store.domain.personnel}
              roles={personnelCompanyRoles}
              subcontractor={
                store.domain.subcontractors[
                  store.domain.personnel?.relationships?.subcontractor?.data?.id
                ]
              }
              teams={orderedDomainTeams}
              user={
                store.domain.users[
                  store.domain.personnel?.relationships?.user?.data?.id
                ]
              }
            />
          </TrainingRegisterResourceManagementContext.Provider>
        </div>
        {coursesMeta.loaded &&
          !store.application.pending.trainingFetch &&
          !store.application.pending.personnelEventsFetch && (
            <div className='col-sm-9 tw-pr-0'>
              {hasReachedTrackedPersonnelUsageThreshold &&
                (['primary', 'administrator'].includes(
                  currentActor.user.attributes.accessType,
                ) ||
                  hasReachedTrackedPersonnelLimit) && (
                  <TrackedPersonnelUsageBanner
                    hasReachedLimit={hasReachedTrackedPersonnelLimit}
                    usage={trackedPersonnelUsage}
                  />
                )}
              <TrainingRegisterResourceManagementContext.Provider
                value={trainingRegisterContextValues}
              >
                <TrainingRecords
                  attachments={Object.values(store.domain.attachments)}
                  autoEnrolmentConfigurations={
                    store.domain.autoEnrolmentConfigurations
                  }
                  autoEnrolmentExclusions={store.domain.autoEnrolmentExclusions}
                  bookers={store.domain.bookers}
                  courseCompanyRoles={courseCompanyRoles}
                  courses={store.domain.courses}
                  coursesMeta={coursesMeta}
                  creditInfoUnavailable={eLearningAllowance.hasError}
                  currentBookings={currentBookings}
                  currentPersonnel={displayPersonnel}
                  currentRegistrations={Object.values(
                    store.domain.registrations,
                  )}
                  eLearningAllowance={eLearningAllowance}
                  eLearningCourses={store.domain.eLearningCourses}
                  fetchPersonnelEvents={fetchPersonnelEvents}
                  hasPersonnelEventViewableAccess={
                    props.hasPersonnelEventViewableAccess
                  }
                  hasTrainingEditableAccess={props.hasTrainingEditableAccess}
                  onAddBooking={handleAddBooking}
                  onAddELearningBooking={handleAddELearningBooking}
                  onAddRole={openPersonnelSidePanel}
                  onBookingReminderClick={handleBookingReminder}
                  onCourseSelectChange={handleCourseSelectChange}
                  onCourseSelectError={breadBoard.addInedibleToast}
                  onCreateAutoEnrolmentExclusion={createAutoEnrolmentExclusion}
                  onDeleteAutoEnrolmentExclusion={deleteAutoEnrolmentExclusion}
                  onDeleteCourseClick={handleDeleteCourseClick}
                  onDeleteTrainingClick={handleDeleteTrainingClick}
                  onEditTrainingClick={handleEditTrainingClick}
                  onNewTrainingClick={handleNewTrainingClick}
                  onRemoveBooking={handleRemoveBooking}
                  onShowBooking={handleViewBookingClick}
                  onTrainingEvidenceProcessed={handleTrainingEvidenceProcessed}
                  onUserCourseCreate={createUserCourse}
                  onUserCourseUpdate={handleUserCourseUpdate}
                  personnelCourseConnectionRequirementPriorities={
                    store.domain.personnelCourseConnectionRequirementPriorities
                  }
                  personnelEvents={{
                    collection: store.domain.personnelEvents,
                    meta: store.domain.personnelEventsMeta,
                    profilePhotos: store.domain.personnelEventProfilePhotos,
                    users: store.domain.personnelEventUsers,
                  }}
                  roles={personnelCompanyRoles}
                  simpleUsers={store.domain.simpleUsers}
                  training={allTraining}
                  trainingEvidenceProcessed={trainingEvidenceProcessed}
                  userCourses={Object.values(store.domain.userCourses)}
                />
              </TrainingRegisterResourceManagementContext.Provider>
            </div>
          )}
        <TrainingRegisterResourceManagementContext.Provider
          value={trainingRegisterContextValues}
        >
          <PersonnelSidePanel
            assignableCompanies={store.domain.assignableCompanies}
            assignableRoles={
              store.application.pending.assignableRolesFetch ?
                []
              : store.domain.assignableRoles
            }
            closeSidePanel={closePersonnelSidePanel}
            currentPersonnel={formPersonnel}
            currentSubcontractor={currentSubcontractor}
            division={
              store.domain.companies[
                store.domain.personnel?.relationships?.company?.data?.id
              ]
            }
            domainLineManager={Object.values(store.domain.lineManagers).find(
              (personnel) => personnel.id === formPersonnel.lineManagerId,
            )}
            domainProfilePhoto={selectedProfilePhoto}
            domainTeams={store.domain.teams}
            isProfilePhotoLoading={isProfilePhotoUploading}
            isSubcontractor={isSubcontractor}
            onAssignableRoleAdd={handleAssignableRoleAdd}
            onCancel={handleCancel}
            onCompanyRolesOptionChange={handleCompanyRolesOptionChange}
            onDateFieldChange={handleDateFieldChange}
            onDeleteClick={handleDeletePersonnelClick}
            onDivisionOptionChange={handleDivisionOptionChange}
            onEditClick={() => setPersonnelSidePanelContext('edit')}
            onFieldOptionChange={handleFieldOptionChange}
            onNewCompanyRoleAdd={handleNewCompanyRoleAdd}
            onPersonnelDateChange={handlePersonnelDateChange}
            onPersonnelFieldChange={handleFieldValueInputChange}
            onPersonnelInputChange={handlePersonnelInputChange}
            onPersonnelOptionChange={handlePersonnelOptionChange}
            onPersonnelUpdateSubmit={handlePersonnelUpdateSubmit}
            onPhotoModalOpen={() =>
              dispatch({ type: 'PROFILE_PHOTO_MODAL_OPEN' })
            }
            onRemoveRole={handleRemoveRole}
            onSelectPrimary={handleSelectPrimary}
            onSubcontractorSelect={handleSubcontractorSelect}
            onTeamSelect={handleTeamSelect}
            onTeamSelectionDelete={handleTeamSelectionDelete}
            onTeamSelectionRoleChange={handleTeamSelectionRoleChange}
            onToggle={handleToggle}
            personnel={store.domain.personnel}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            roles={personnelCompanyRoles}
            setCurrentPersonnel={setFormPersonnel}
            setIsSubcontractor={setIsSubcontractor}
            sidePanelContext='edit'
            sidePanelIsOpen={isPersonnelSidePanelOpen}
            submitDisabled={submitDisabled}
            user={
              store.domain.users[
                store.domain.personnel?.relationships?.user?.data?.id
              ]
            }
          />
        </TrainingRegisterResourceManagementContext.Provider>
        <TrainingRegisterResourceManagementContext.Provider
          value={trainingRegisterContextValues}
        >
          <TrainingSidePanel
            closeSidePanel={closeTrainingSidePanel}
            createTraining={createTraining}
            currentTraining={currentTraining}
            domainCourse={currentTraining.course}
            includedAttachments={Object.values(store.domain.attachments)}
            includedCoverImages={Object.values(store.domain.coverImages)}
            isProfilePhotoUploading={isProfilePhotoUploading}
            onCancelTraining={handleCancelTraining}
            onInputChange={handleTrainingInputChange}
            onTrainingCalendarClose={handleTrainingCalendarClose}
            onTrainingDateChange={handleTrainingDateChange}
            personnelId={props.personnelId}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            resetRequestError={resetRequestError}
            setSidePanelIsOpen={setIsTrainingSidePanelOpen}
            sidePanelContext={trainingSidePanelContext}
            sidePanelIsOpen={isTrainingSidePanelOpen}
            submitDisabled={submitDisabled}
            updateTraining={updateTraining}
          />
        </TrainingRegisterResourceManagementContext.Provider>

        <TrainingRequirementsSidePanel
          closeSidePanel={closeTrainingRequirementsSidePanel}
          createTrainingRequirement={createTrainingRequirement}
          currentTraining={currentTraining}
          currentTrainingRequirement={currentTrainingRequirement}
          domainCourse={currentTraining.course}
          includedAttachments={Object.values(store.domain.attachments)}
          includedCoverImages={Object.values(store.domain.coverImages)}
          onTrainingCalendarClose={handleTrainingCalendarClose}
          onTrainingDateChange={handleTrainingDateChange}
          onTrainingInputChange={handleTrainingInputChange}
          onTrainingRequirementInputChange={
            handleTrainingRequirementInputChange
          }
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          resetRequestError={resetRequestError}
          setSidePanelIsOpen={setIsTrainingRequirementsSidePanelOpen}
          sidePanelContext={trainingRequirementsSidePanelContext}
          sidePanelIsOpen={isTrainingRequirementsSidePanelOpen}
          submitDisabled={submitDisabled}
        />
        <TrainingRegisterResourceManagementContext.Provider
          value={trainingRegisterContextValues}
        >
          <BookingSidePanel
            currentBooking={currentBooking}
            domainBooker={selectedBooker}
            domainBookingSource={selectedBooking}
            domainCourse={selectedBookingCourse}
            domainPersonnel={formPersonnel}
            domainPersonnelMeta={bookingGroupPersonnelMeta}
            onBookingReminderClick={handleBookingReminder}
            onBookingUpdateSubmit={handleBookingUpdateSubmit}
            onCancel={handleCloseBookingSidePanel}
            onDateChange={handleBookingDateChange}
            onEditBookingClick={handleEditBookingClick}
            onInputChange={handleBookingInputChange}
            onRecordTraining={() =>
              handleNewTrainingClick({
                booking: selectedBooking,
                course: selectedBookingCourse,
              })
            }
            onRemoveBooking={handleRemoveBooking}
            onSubmit={createBooking}
            removeErrorStyling={removeErrorStyling}
            requestError={requestError}
            showBookingGroupView={false}
            sidePanelContext={bookingSidePanelContext}
            sidePanelIsOpen={isBookingSidePanelOpen}
            submitDisabled={submitDisabled}
          />
        </TrainingRegisterResourceManagementContext.Provider>
        {bookingELearningModal.course && (
          <BookingELearningModal
            course={bookingELearningModal.course}
            createELearningBooking={createELearningBooking}
            currentCredits={eLearningAllowance.currentCredits}
            eLearningCourse={bookingELearningModal.eLearningCourse}
            identifier={displayPersonnel.firstName}
            isOpen={bookingELearningModal.isOpen}
            setIsClosed={() => {
              setBookingELearningModal(defaultBookingELearningModal);
            }}
          />
        )}
        {eLearningAllowance.loaded &&
          eLearningAllowance.currentCredits <= 0 && (
            <BookingELearningNoCreditsModal
              isOpen={bookingELearningNoCreditsModal.isOpen}
              setIsClosed={() => {
                setBookingELearningNoCreditsModal(
                  defaultBookingELearningNoCreditsModal,
                );
              }}
            />
          )}
        <DestroyModal
          additionalContent={
            hasELearningBookings ?
              <p className='tw-m-0 tw-mt-5 tw-rounded-lg tw-border-0 tw-bg-red-025 tw-p-3 tw-text-red-800'>
                {i18nTComponentsShared('destroyModal.additionalContent')}
              </p>
            : null
          }
          confirmationText={i18nTCommon('shared.deleteName', {
            interpolation: { escapeValue: false },
            name: displayPersonnel.firstName,
          })}
          displayText={i18nTCommon('shared.areYouSureYouWantToDelete', {
            interpolation: { escapeValue: false },
            name: displayPersonnel.firstName,
          })}
          isOpen={isDestroyModalOpen}
          onClose={() => setIsDestroyModalOpen(false)}
          onDestroy={destroyPersonnel}
        />
        <TrainingDestroyModal
          currentTraining={currentTraining}
          deleteTraining={deleteTraining}
          isOpen={isTrainingDestroyModalOpen}
          setIsOpen={setIsTrainingDestroyModalOpen}
        />

        <CourseDestroyModal
          currentTraining={currentTraining}
          deleteUserCourse={deleteUserCourse}
          isOpen={isCourseDestroyModalOpen}
          setIsOpen={setIsCourseDestroyModalOpen}
        />
        <BookingDestroyModal
          autoEnrolmentExclusion={selectedBookingAutoEnrolmentExclusion}
          booking={selectedBooking}
          course={selectedBookingCourse}
          isOpen={isBookingDestroyModalOpen}
          onClose={handleDestroyModalClose}
          onDestroy={deleteBooking}
          personnel={displayPersonnel}
        />
        <BookingReminderModal
          booking={selectedBooking}
          course={selectedBookingCourse}
          isOpen={isBookingReminderModalOpen}
          onClose={handleReminderModalClose}
          onSendReminderConfirmation={sendBookingReminder}
          personnel={displayPersonnel}
        />
        {!props.isViewingOwnProfile && (
          <SendInvitationModal
            isOpen={isSendInvitationModalOpen}
            onClose={handleSendInvitationModalClose}
            onSendConfirmationClick={handleSendConfirmationClick}
            personnel={store.domain.personnel}
            user={
              store.domain.users[
                store.domain.personnel?.relationships?.user?.data?.id
              ]
            }
          />
        )}
        {isUnarchivingBlocked ?
          <UnarchiveBlockedModal
            closeModal={closeUnarchiveBlockedModal}
            isOpen={isUnarchiveBlockedModalOpen}
            usage={trackedPersonnelUsage}
          />
        : <ArchiveModal
            closeModal={closeArchiveModal}
            hasELearningBookings={hasELearningBookings}
            isArchived={isArchived}
            isOpen={isArchiveModalOpen}
            longName={personDisplayName({ attributes: displayPersonnel })}
            onArchive={archivePersonnel}
            onUnarchive={unarchivePersonnel}
            shortName={displayPersonnel.firstName}
          />
        }
        <Portal containerSelector='[data-portal="page-title"]'>
          {personDisplayName({ attributes: displayPersonnel })}
        </Portal>

        {isProfilePhotoModalOpen && (
          <ProfilePhotoModal
            currentPersonnel={formPersonnel}
            domainProfilePhoto={selectedProfilePhoto}
            key={currentProfilePhoto?.id}
            onCropReady={handleProfilePhotoCropReady}
            onMediaLoaded={(mediaSize) =>
              handleProfilePhotoMediaChange(mediaSize)
            }
            onPhotoModalClose={handlePhotoModalClose}
            onPhotoRemoved={() => {
              handleProfilePhotoRemoval();
              resetProfilePhotoUploadState();
            }}
            onProcessProfilePhoto={(photoFile) => {
              dispatch({ type: 'PROFILE_PHOTO_MODAL_CLOSE' });
              // skip it if photo was removed.
              if (!!currentProfilePhoto && !!photoFile) {
                handleProcessProfilePhoto(photoFile);
              }
            }}
          />
        )}
      </TrackedPersonnelContext.Provider>
    )
  );
}
